import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { DASHBOARD } from '@constants/routes';
import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import {
  buildSelfDeclarationAnalytics,
  trackSelfDeclarationFormOpened,
  trackSelfDeclarationFormSubmitted
} from '@features/authentication/helpers/kycAnalytics';
import { useLazyGetOrganizationListQuery } from '@features/authentication/queries/AuthQuery';
import {
  useInitiateESignMutation,
  useLazyGetESignCompletedStatusQuery,
  useLazyGetESignDetailsQuery,
  useLazyGetESignDocumentsQuery,
  useLazyGetESignStatusQuery
} from '@features/e-sign/queries/ESignQuery';
import useNotify from '@hooks/notify';
import { setCurrentOrg } from '@layouts/main-header/userSlice';
import { DigioCallbackResponse, launchDigioSign } from '@utils/digio';
import { getLocalStorageItem } from '@utils/storage';
import { Org } from 'types/organization';

const POLL_INTERVAL = 2000;
const MAX_POLL_DURATION = 60_000;

const useESign = (orgId: string) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { createErrorAlert } = useNotify();
  const { currentUser, currentOrg } = useSelector((state: any) => state.user);

  const [isLoading, setIsLoading] = useState(false);
  const abortRef = useRef(false);

  const [getDocuments] = useLazyGetESignDocumentsQuery();
  const [initiateESign] = useInitiateESignMutation();
  const [getStatus] = useLazyGetESignStatusQuery();
  const [getDetails] = useLazyGetESignDetailsQuery();
  const [getCompletedStatus] = useLazyGetESignCompletedStatusQuery();
  const [getOrgList] = useLazyGetOrganizationListQuery();

  const refreshOrg = async () => {
    const resp = await getOrgList(null, false);
    if ('data' in resp) {
      const organizationId = getLocalStorageItem(
        LOCAL_STORAGE_KEYS.ORGANIZATION_ID
      );
      const org = resp.data?.find((o: Org) => o.id === organizationId);
      if (org) dispatch(setCurrentOrg(org));
    }
  };

  const pollStatus = (documentId: string): Promise<void> =>
    new Promise((resolve, reject) => {
      const startTime = Date.now();

      const poll = async () => {
        if (abortRef.current) {
          reject(new Error('Polling aborted'));
          return;
        }

        if (Date.now() - startTime > MAX_POLL_DURATION) {
          reject(new Error('E-sign status polling timed out'));
          return;
        }

        try {
          const { data } = await getStatus({ orgId, documentId });
          if (data?.status && data.status === 'pending') {
            resolve();
            return;
          }
          setTimeout(poll, POLL_INTERVAL);
        } catch {
          reject(new Error('Failed to check e-sign status'));
        }
      };

      poll();
    });

  const pollCompletedStatus = (documentId: string): Promise<void> =>
    new Promise((resolve, reject) => {
      const startTime = Date.now();

      const poll = async () => {
        if (abortRef.current) {
          reject(new Error('Polling aborted'));
          return;
        }

        if (Date.now() - startTime > MAX_POLL_DURATION) {
          reject(new Error('E-sign completion polling timed out'));
          return;
        }

        try {
          const { data } = await getCompletedStatus({ orgId, documentId });
          if (data?.status && data.status === 'signed') {
            resolve();
            return;
          }
          setTimeout(poll, POLL_INTERVAL);
        } catch {
          reject(new Error('Failed to check e-sign completion status'));
        }
      };

      poll();
    });

  const signFlow = async (documentId: string, isBankKyc: boolean = false) => {
    await pollStatus(documentId);

    const detailsResp = await getDetails({ orgId, documentId });
    if ('error' in detailsResp || !detailsResp.data) {
      createErrorAlert('Failed to fetch e-sign details');
      return;
    }

    const { token_id: tokenId, provider_document_id: formId } =
      detailsResp.data;
    const customerIdentifier =
      process.env.REACT_APP_DIGIO_MOBILE_NUMBER ||
      currentUser?.mobile?.replace('91', '');

    if (!customerIdentifier) {
      createErrorAlert('User phone number not available');
      return;
    }

    const selfDeclarationAnalytics = buildSelfDeclarationAnalytics(
      currentUser,
      currentOrg
    );
    trackSelfDeclarationFormOpened(selfDeclarationAnalytics);

    launchDigioSign(formId, customerIdentifier, tokenId, {
      callback: async (response: DigioCallbackResponse) => {
        if (response.error_code) {
          createErrorAlert(response.error_message || 'E-sign signing failed');
          setIsLoading(false);
          return;
        }

        try {
          await pollCompletedStatus(documentId);
          trackSelfDeclarationFormSubmitted(selfDeclarationAnalytics);
          if (isBankKyc) window.location.reload();
          else await refreshOrg();

          navigate(DASHBOARD);
        } catch (err: any) {
          createErrorAlert(err?.message || 'Something went wrong after e-sign');
        }
      }
    });
  };

  const initiateAndSign = async (
    documentId: string,
    isBankKyc: boolean = false
  ) => {
    const initiateResp = await initiateESign({
      orgId,
      document_id: documentId
    });
    if ('error' in initiateResp) {
      createErrorAlert('Failed to initiate e-sign');
      return;
    }

    await signFlow(documentId, isBankKyc);
  };

  const handleESign = async () => {
    if (!orgId) return;
    setIsLoading(true);
    abortRef.current = false;

    try {
      const docResp = await getDocuments(orgId);
      if ('error' in docResp || !docResp.data?.documents?.length) {
        createErrorAlert('Failed to fetch e-sign documents');
        setIsLoading(false);
        return;
      }

      const document = docResp.data.documents?.filter(
        (doc: any) => doc.status !== 'signed'
      )[0];

      if (!document) {
        createErrorAlert('No e-sign document found');
        return;
      }

      const { document_id: documentId, status: documentStatus } =
        document || {};

      if (documentStatus === 'created') {
        await initiateAndSign(documentId);
      } else {
        await signFlow(documentId);
      }
    } catch (error: any) {
      createErrorAlert(error?.message || 'Something went wrong during e-sign');
    }
  };

  const handleESignForDocument = async (
    documentId: string,
    documentStatus: string
  ) => {
    if (!orgId || !documentId) return;
    setIsLoading(true);
    abortRef.current = false;

    try {
      if (documentStatus === 'created') {
        await initiateAndSign(documentId, true);
      } else {
        await signFlow(documentId, true);
      }
    } catch (error: any) {
      createErrorAlert(error?.message || 'Something went wrong during e-sign');
      setIsLoading(false);
    }
  };

  return { handleESign, handleESignForDocument, isLoading };
};

export default useESign;
